<script setup>
import { computed } from 'vue';
import { useMainStore } from '../../../store/index.js';
import ColorOption from './ColorOption';
import ColorOptionInfo from './ColorOptionInfo';
import ConfigurationPackageSelectionRequired from '../../ConfigurationPackageSelectionRequired';
import ConfigurationRadioSelection from '../../ConfigurationRadioSelection';
import ConfigurationCheckSelection from '../../ConfigurationCheckSelection';
import '../../../styles/configurator-section.css';
import '../../../styles/configurator-selection.css';

const store = useMainStore();

const emit = defineEmits(['select']);

const props = defineProps({
  items: {
    type: Object,
    default: null,
  },
  frontImage: {
    type: String,
    required: true,
  },
  showInformation: {
    type: Boolean,
    default: false,
  },
  showAsSubheader: {
    type: Boolean,
    default: false,
  },
});

const standardColourOptions = computed(() => props.items[0].options.filter(o => !o.information));
const otherColourOptions = computed(() => props.items[0].options.filter(o => o.information));

const selectedColor = computed(() => {
  return store.selected.cabColour.cabColour;
});

const handleSelect = (option) => {
  store.selected.cabColour.cabColour = option;
  emit('select', { option, type: 'cabColour' });
};

const handleOtherSelect = ({ option }) => {
  store.selected.cabColour.cabColour = option;
  emit('select', { option, type: 'cabColour' });
};

const cabAirDeflectors = computed(() => {
  if (!props.items) return null;
  return props.items.find(x => x.id === 'cabAirDeflectors');
});

const specType = computed(() => {
  return store.misc?.specType?.trim()?.toLowerCase();
});

const cabColorHeaderTitle = computed(() => {
  return store.sectionMetadata?.cabColour?.title;
});

const showCabColorSubheaderTitle = computed(() => {
  return props.items.length > 1;
});

const cabColorSubheaderTitle = computed(() => {
  return props.items.find(x => x.id === 'cabColour')?.title;
});
</script>

<template>
  <section
    v-if="props.items"
    id="cab-color"
    class="configurator-section"
  >
    <h3
      v-if="showAsSubheader"
      class="sdds-u-m0"
    >
      {{ $i18n(cabColorHeaderTitle) }}
    </h3>
    <h2 v-else>
      {{ $i18n(cabColorHeaderTitle) }}
    </h2>
    <h3
      v-if="showCabColorSubheaderTitle"
      class="sdds-u-m0"
    >
      {{ $i18n(cabColorSubheaderTitle) }}
    </h3>
    <div class="configurator-section__cab-color">
      <div class="configurator-section__image">
        <img
          :src="props.frontImage"
          alt="Truck - front side view"
        >
      </div>
      <div class="cab-color-options-wrapper">
        <div
          class="configurator-selection color-configurator-selection"
          :class="{ 'color-configurator-selection-with-other-selection': !!otherColourOptions.length }"
        >
          <template v-if="specType !== 'stock_vehicle'">
            <ColorOption
              v-for="item in standardColourOptions"
              :key="item.id"
              :option="item"
              :selected="selectedColor.id === item.id"
              :selected-payment-type="store.selectedPaymentType"
              :show-information="props.showInformation"
              @select="handleSelect"
            />
          </template>
          <template v-if="specType === 'stock_vehicle'">
            <ColorOptionInfo
              v-for="item in props.items[0].options"
              :key="item.id"
              :option="item"
              :selected="selectedColor.id === item.id"
              :show-information="props.showInformation"
              @select="handleSelect"
            />
          </template>
        </div>
        <template v-if="!!otherColourOptions.length">
          <ConfigurationPackageSelectionRequired
            v-model="store.selected.cabColour.cabColour"
            :item="{
              id: 'otherColour',
              title: 'otherColourSubheaderTitle',
              description: 'otherColourDescription',
              options: otherColourOptions,
            }"
            @select="handleOtherSelect"
          />
        </template>
      </div>
    </div>
    <template v-if="cabAirDeflectors">
      <div class="configurator-section__row margin-top--3">
        <ConfigurationCheckSelection
          v-if="cabAirDeflectors.selectionType === 'checkbox'"
          v-model="store.selected.cabColour[cabAirDeflectors.id]"
          :type="cabAirDeflectors.id"
          :title="cabAirDeflectors.title"
          :options="cabAirDeflectors.options"
          @select="emit('select', $event)"
        />
        <ConfigurationRadioSelection
          v-if="cabAirDeflectors.selectionType === 'radio'"
          v-model="store.selected.cabColour[cabAirDeflectors.id]"
          :type="cabAirDeflectors.id"
          :options="cabAirDeflectors.options"
          :title="cabAirDeflectors.title"
          @select="emit('select', $event)"
        />
        <!-- <template v-if="cabAirDeflectors">
          <div class="sdds-divider-dark-vertical" />
          <ConfigurationCheckSelection
            v-model="store.selected.cabColour[cabAirDeflectors.id]"
            :type="cabAirDeflectors.id"
            :title="cabAirDeflectors.title"
            :options="cabAirDeflectors.options"
            @select="emit('select', $event)"
          />
        </template> -->
      </div>
    </template>
  </section>
</template>

<style scoped>
  .configurator-section__cab-color {
    display: flex;
    flex-direction: row;
    place-content: center;
  }
  .cab-color-options-wrapper {
    display: flex;
    flex-direction: column;
    gap: 1em;
    place-items: center;
  }
  .configurator-section__image {
    display: flex;
    place-content: center;
    width: 60%;
  }
  .configurator-section__image img {
    max-width: 100%;
    object-fit: contain;
  }
  .color-configurator-selection {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    grid-template-rows: repeat(4, 1fr);
    gap: var(--sdds-spacing-element-32);
    width: 80%;
    padding-top: var(--sdds-spacing-element-48);
  }
  .color-configurator-selection-with-other-selection {
    grid-template-columns: repeat(3, 1fr);
    grid-template-rows: repeat(2, 1fr);
  }

  @media only screen and (min-width: 1295px) {
    .margin-top--3 {
      margin-top: -3em;
    }
  }

  @media only screen and (max-width: 1294px) {
    .configurator-section__cab-color {
      flex-direction: column;
      align-items: center;
    }
    .cab-color-options-wrapper {
      width: 100%;
    }
    .color-configurator-selection {
      display: flex;
      flex-direction: row;
      flex-wrap: wrap;
      justify-content: center;
      gap: var(--sdds-spacing-element-24);
      width: 100vw;
    }
  }

</style>
